import { useRecoilValue, useRecoilCallback } from "recoil";
import styled, { ThemeProvider } from "styled-components";
import { defaultButtonTheme, focusVisible, text } from "@quickpoint.me/shared";
import { DemoMode, ShowTourSelectionAtom } from "../state/uiAtoms";
import type { TourVariant } from "../lib/tour/tour";
import {
	type TourSection,
	tourSectionStartStep,
} from "../lib/tour/variants/accessibility/sections";
import { Authorization } from "../state/authAtoms";
import { ShowStacksDuringDemoMode } from "../state/stackAtoms";
import PlayIcon from "../icons/play.svg?react";
import InfoIcon from "../icons/info-circle.svg?react";
import { useMetrics } from "../hooks/useMetrics";
import { useStartTour } from "../hooks/useStartTour";
import { useEffect, useRef, useState } from "react";

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2001;
`;

const ModalContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  padding: 40px;
  border-radius: 8px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  font-family: "Gill Sans", sans-serif;
  display: flex;
  flex-direction: column;
  gap: 24px;
  z-index: 2002;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  margin: 0;
  text-align: center;
  font-weight: normal;
  color: #333;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #999;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;

  &:hover {
    color: #333;
  }

  ${focusVisible}
`;

const PrimaryCard = styled.button`
  background: white;
  border: 2px solid #333;
  border-radius: 8px;
  padding: 30px 24px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:hover {
    box-shadow: 0 5px 15px rgba(0,0,0,0.12);
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }

  svg {
    height: 48px;
    width: 48px;
    fill: #333;
  }

  ${focusVisible}
`;

const PrimaryTitle = styled.h3`
  margin: 0;
  font-size: 1.3rem;
  font-weight: bold;
  color: #333;
`;

const Duration = styled.span`
  font-size: 0.85rem;
  color: #888;
  font-style: italic;
`;

const Description = styled.p`
  margin: 0;
  text-align: center;
  font-size: 0.95rem;
  color: #666;
  line-height: 1.4;
`;

const MoreToursToggle = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 4px 0;
  align-self: center;

  &:hover {
    color: #333;
  }

  ${focusVisible}
`;

const SecondaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const SectionIndex = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionIndexLabel = styled.p`
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: #666;
  text-align: center;
`;

const SectionChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
`;

const SectionChip = styled.button`
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 8px 12px;
  min-height: 44px;
  font-size: 0.85rem;
  color: #333;
  cursor: pointer;
  transition: border-color 0.15s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:hover {
    border-color: #333;
  }

  ${focusVisible}
`;

const SecondaryCard = styled.button`
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 16px 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:hover {
    border-color: #333;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }

  svg {
    height: 24px;
    width: 24px;
    fill: #666;
  }

  ${focusVisible}
`;

const SecondaryTitle = styled.h4`
  margin: 0;
  font-size: 0.95rem;
  font-weight: bold;
  color: #555;
`;

const SecondaryDescription = styled.p`
  margin: 0;
  text-align: center;
  font-size: 0.8rem;
  color: #888;
  line-height: 1.3;
`;

function TourSelectionModal() {
	const isOpen = useRecoilValue(ShowTourSelectionAtom);
	const { track } = useMetrics();
	const auth = useRecoilValue(Authorization);
	const dialogRef = useRef<HTMLDivElement>(null);
	const [showMore, setShowMore] = useState(false);

	const startTour = useStartTour();

	const exitDemoMode = useRecoilCallback(({ set }) => () => {
		set(ShowTourSelectionAtom, false);
		set(ShowStacksDuringDemoMode, false);
		if (auth.isLoggedIn) set(DemoMode, false);
	});

	const handleSelect = (variant: TourVariant) => {
		track('tour_started', { variant });
		startTour(variant);
	};

	// section index into the Full Tour (the accessibility variant) — jump
	// straight to a section instead of clicking through from the start
	const fullTourSections: { section: TourSection; label: string }[] = [
		{ section: "basics", label: text.tourSteps.contentsGettingStarted },
		{ section: "stories", label: text.tourSteps.contentsStories },
		{ section: "formatting", label: text.tourSteps.contentsFormattingText },
		{ section: "links", label: text.tourSteps.contentsLinksAndBacklinks },
		{ section: "images", label: text.tourSteps.contentsAddingImages },
		{ section: "charts", label: text.tourSteps.contentsCharts },
		{ section: "publishing", label: text.tourSteps.contentsSharingAndPublishing },
		{ section: "split", label: text.tourSteps.contentsMasteringLayouts },
		{ section: "fx", label: text.tourSteps.contentsMasteringFx },
	];

	const handleSectionSelect = (section: TourSection) => {
		track("tour_started", { variant: "accessibility", section });
		startTour("accessibility", tourSectionStartStep(section));
	};

	const handleClose = () => {
		exitDemoMode();
	};

	// Focus trap and keyboard handling
	useEffect(() => {
		if (!isOpen) return;

		const dialog = dialogRef.current;
		if (!dialog) return;

		// Focus the first option card
		const firstButton = dialog.querySelector<HTMLButtonElement>("button");
		firstButton?.focus();

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				handleClose();
				return;
			}

			// Focus trap: Tab cycles within the dialog
			if (e.key === "Tab") {
				const focusable = dialog.querySelectorAll<HTMLElement>(
					'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
				);
				const first = focusable[0];
				const last = focusable[focusable.length - 1];

				if (e.shiftKey && document.activeElement === first) {
					e.preventDefault();
					last.focus();
				} else if (!e.shiftKey && document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<ThemeProvider theme={defaultButtonTheme}>
			<Backdrop onClick={handleClose} />
			<ModalContainer
				ref={dialogRef}
				role="dialog"
				aria-labelledby="tour-modal-title"
				aria-modal="true"
				data-testid="tour-selection-modal"
				onClick={(e) => e.stopPropagation()}
			>
				<TitleRow>
					<Title id="tour-modal-title">{text.ui.chooseTour}</Title>
					<CloseButton
						onClick={handleClose}
						aria-label={text.ui.closeTourSelection}
					>
						&times;
					</CloseButton>
				</TitleRow>

				<PrimaryCard
					onClick={() => handleSelect("ai-first")}
					aria-label={text.tour.aiPoweredLabel}
				>
					<PlayIcon aria-hidden="true" />
					<PrimaryTitle>{text.tour.aiPowered}</PrimaryTitle>
					<Duration>{text.tour.aiPoweredDuration}</Duration>
					<Description>{text.tour.aiPoweredDescription}</Description>
				</PrimaryCard>

				<MoreToursToggle
					onClick={() => setShowMore(!showMore)}
					aria-expanded={showMore}
				>
					{showMore ? text.tour.fewerTours : text.tour.moreTours}
				</MoreToursToggle>

				{showMore && (
					<SecondaryGrid>
						<SecondaryCard
							onClick={() => handleSelect("a11y")}
							aria-label={text.tour.accessibilityLabel}
						>
							<InfoIcon aria-hidden="true" />
							<SecondaryTitle>{text.tour.accessibility}</SecondaryTitle>
							<Duration>{text.tour.accessibilityDuration}</Duration>
							<SecondaryDescription>
								{text.tour.accessibilityDescription}
							</SecondaryDescription>
						</SecondaryCard>

						<SecondaryCard
							onClick={() => handleSelect("accessibility")}
							aria-label={text.tour.fullTourLabel}
						>
							<InfoIcon aria-hidden="true" />
							<SecondaryTitle>{text.tour.fullTour}</SecondaryTitle>
							<Duration>{text.tour.fullTourDuration}</Duration>
							<SecondaryDescription>
								{text.tour.fullTourDescription}
							</SecondaryDescription>
						</SecondaryCard>
					</SecondaryGrid>
				)}

				{showMore && (
					<SectionIndex>
						<SectionIndexLabel id="tour-section-index-label">
							{text.tour.jumpToSection}
						</SectionIndexLabel>
						<SectionChips
							role="group"
							aria-labelledby="tour-section-index-label"
						>
							{fullTourSections.map(({ section, label }) => (
								<SectionChip
									key={section}
									onClick={() => handleSectionSelect(section)}
									aria-label={text.tour.jumpToSectionLabel(label)}
									data-testid={`tour-section-${section}`}
								>
									{label}
								</SectionChip>
							))}
						</SectionChips>
					</SectionIndex>
				)}
			</ModalContainer>
		</ThemeProvider>
	);
}

export default TourSelectionModal;
